import * as React from "react";

import { Dimensions, useDebouncedCallback, useMeasure } from "@/shared/hooks";

/**
 * Counts the controls a toolbar will render. Fragments are seen through;
 * absent children (`false`, `null`) are not counted.
 *
 * {@link useCollapseTools} uses this count to notice that a collapsed
 * toolbar's contents changed without measuring its DOM. A control that
 * changes width without mounting or unmounting is invisible to the count,
 * so a collapsed toolbar will not react to it.
 */
export function countTools(node: React.ReactNode): number {
  let count = 0;
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child)) {
      return;
    }
    if (child.type === React.Fragment) {
      count += countTools((child.props as React.PropsWithChildren).children);
      return;
    }
    count += 1;
  });
  return count;
}

/**
 * Reports whether the toolbar is too wide for the panel and should collapse
 * into a popover.
 *
 * Only the *uncollapsed* toolbar is ever measured; measuring the collapsed
 * one would read the overflow button's width, expand, and oscillate. The
 * stored width is tied to the control count it was measured for: when
 * `toolCount` changes, the width is discarded and the toolbar renders
 * expanded until it is measured again. That keeps a toolbar that loses a
 * control while collapsed (e.g. the 3D panel's Clear-measurements control
 * disappearing) from staying collapsed forever.
 *
 * @param props.collapseRatio fraction of the row width the toolbar may
 * occupy before it collapses.
 * @param props.toolCount how many controls the toolbar renders, from
 * {@link countTools}.
 *
 * @returns the collapse flag, plus measurement callbacks to attach to the
 * panel's header row and to the uncollapsed toolbar.
 */
export function useCollapseTools(props: {
  collapseRatio: number;
  toolCount: number;
}) {
  const { collapseRatio, toolCount } = props;
  const { rowWidth, measuredRow } = hookHelpers.useMeasureRow();
  const { toolsWidth, measuredTools } = hookHelpers.useMeasureTools(toolCount);

  const shouldCollapse = React.useMemo(
    // An unmeasured toolbar has width 0 and therefore fits.
    () => toolsWidth > rowWidth * collapseRatio,
    [rowWidth, toolsWidth, collapseRatio],
  );
  return { shouldCollapse, measuredRow, measuredTools };
}

const hookHelpers = {
  useMeasureRow: () => {
    const [rowWidth, setRowWidth] = React.useState(0);
    const undebounced = React.useCallback(
      ({ width }: Dimensions) => {
        setRowWidth(width);
      },
      [setRowWidth],
    );
    const onDimensionsChange = useDebouncedCallback(undebounced, 10);
    const [measuredRow] = useMeasure({ onDimensionsChange });
    return { rowWidth, measuredRow };
  },

  /**
   * Measures the uncollapsed toolbar, stamping each width with the control
   * count it was measured for. A width stored for a different count reads
   * as 0 — unmeasured — until the expanded toolbar is observed again.
   *
   * Zero-width reports are dropped: the observer keeps reporting a detached
   * node after the toolbar collapses, and 0 would read as "fits" forever.
   */
  useMeasureTools: (toolCount: number) => {
    const [measured, setMeasured] = React.useState({ width: 0, toolCount });
    // Updated every render so the debounced report below stamps the width
    // with the current count, without re-creating the observer.
    const measuredFor = React.useRef(toolCount);
    measuredFor.current = toolCount;
    const undebounced = React.useCallback(({ width }: Dimensions) => {
      if (width > 0) {
        setMeasured({ width, toolCount: measuredFor.current });
      }
    }, []);
    const onDimensionsChange = useDebouncedCallback(undebounced, 10);
    const [measuredTools] = useMeasure({ onDimensionsChange });
    return {
      toolsWidth: measured.toolCount === toolCount ? measured.width : 0,
      measuredTools,
    };
  },
};
